import axios from 'axios';
import { withScopeIdEnabled } from './saved-scopes.helpers';
import { create } from 'zustand';
import { z } from 'zod';
import { useSelectedOrganizationName } from '@client/store/organization';
import { getHmrStoreState, initHmrStore } from '@client/helpers/vite.helpers';
import { BUILTIN_SCOPE_IDS } from '@shared/models/scope-preset.model';

const STORE_NAME = 'organization-scope-settings';
type Settings = { ids: string[]; confirmedIds?: string[]; error: boolean };
type State = { organizations: Record<string, Settings> };
const useSettings = create<State>(() => ({
  organizations: {},
  ...getHmrStoreState<State>(STORE_NAME),
}));
initHmrStore(STORE_NAME, useSettings);
const DEFAULT_IDS = [...BUILTIN_SCOPE_IDS];
const configFor = (organization: string) => ({
  headers: { Organization: organization },
});
type Toggle = { id: string; enabled: boolean };
const pending = new Map<string, Toggle[]>();
const writes = new Map<string, Promise<void>>();
const revisions = new Map<string, number>();
const confirmedIds = (organization: string): string[] => {
  const settings = useSettings.getState().organizations[organization];
  return settings?.confirmedIds ?? settings?.ids ?? DEFAULT_IDS;
};
const setIds = (organization: string, ids: string[], error = false) => {
  const optimisticIds = (pending.get(organization) ?? []).reduce(
    (current, toggle) => withScopeIdEnabled(current, toggle.id, toggle.enabled),
    ids,
  );
  useSettings.setState(({ organizations }) => ({
    organizations: {
      ...organizations,
      [organization]: { ids: optimisticIds, confirmedIds: ids, error },
    },
  }));
};
const finishToggle = (organization: string, toggle: Toggle) => {
  pending.set(
    organization,
    (pending.get(organization) ?? []).filter((item) => item !== toggle),
  );
};
const idsSchema = z.array(z.string().min(1));

/** Load the organization list independently of opening the scope panel. */
export const loadOrganizationScopeSettings = async (
  organization: string,
): Promise<void> => {
  const revision = revisions.get(organization);
  try {
    const { data } = await axios.get<unknown>(
      'projects/scope-settings',
      configFor(organization),
    );
    // A delayed preload/retry must not replace a newer optimistic edit or save.
    if (
      revision !== revisions.get(organization) ||
      pending.get(organization)?.length
    )
      return;
    setIds(organization, idsSchema.parse(data));
  } catch {
    if (revision === revisions.get(organization))
      setIds(organization, confirmedIds(organization), true);
  }
};

/** Show edits immediately and persist them in click order, rolling back only failed edits. */
export const setOrganizationScopeEnabled = (
  organization: string,
  id: string,
  enabled: boolean,
): Promise<void> => {
  const toggle = { id, enabled };
  pending.set(organization, [...(pending.get(organization) ?? []), toggle]);
  revisions.set(organization, (revisions.get(organization) ?? 0) + 1);
  setIds(organization, confirmedIds(organization));
  const write = (writes.get(organization) ?? Promise.resolve()).then(
    async () => {
      try {
        const { data } = await axios.put<unknown>(
          'projects/scope-settings',
          { id, enabled },
          configFor(organization),
        );
        const ids = idsSchema.parse(data);
        finishToggle(organization, toggle);
        setIds(
          organization,
          ids,
          useSettings.getState().organizations[organization]?.error ?? false,
        );
      } catch {
        finishToggle(organization, toggle);
        setIds(organization, confirmedIds(organization), true);
      }
    },
  );
  writes.set(organization, write);
  void write.finally(() => {
    if (writes.get(organization) === write) writes.delete(organization);
    if (!pending.get(organization)?.length) pending.delete(organization);
  });
  return write;
};

export const useOrganizationScopes = () => {
  const organization = useSelectedOrganizationName();
  const settings = useSettings(({ organizations }) =>
    organization ? organizations[organization] : undefined,
  );
  return {
    enabledIds: settings?.ids ?? DEFAULT_IDS,
    error: settings?.error ?? false,
    organization,
  };
};
